// FX keepers: the applied-rate shape and the conversion arithmetic.
//
// A DATED rate table, not a single mutable setting. The original design had one
// settings row holding "the" USD→CAD rate, which meant every historical report
// silently changed the moment someone updated it — a closed quarter would
// quietly restate itself. Dating the rate makes a report over a past period pin
// to the rate as of that period, and every report response echoes the rate and
// its `as_of` so any number can be reproduced by hand.
//
// KEEPERS ONLY. `FxRate` and its write shapes are generated
// (src/gen/client.ts) and re-exported by src/facade.ts.
import { z } from "zod";
import { IsoDate } from "./common.js";
import { CurrencySchema } from "./gen/client.js";
/**
 * What every currency-converting report echoes: which rate was applied and
 * when it took effect. `as_of` is the effective_date of the row that won, NOT
 * the date the report was run — that distinction is the whole point.
 *
 * Hand-written because the document spells this shape inline on each report
 * (`DriftReportFx`, `ImplementationFeeReportFx`, …) rather than as one named
 * component. The SPA renders all of them through one component, so it needs one
 * name for the shape they share.
 */
export const FxApplied = z.object({
    target_currency: CurrencySchema,
    usd_to_cad: z.number().positive(),
    as_of: IsoDate,
});
/**
 * What the reports that convert for DISPLAY ONLY echo — the bill run, the
 * calendar and the billing lineup.
 *
 * Those three are about WORK, not about currency. Their totals are a list, one
 * entry per source currency, and they convert nothing: an invoice may not mix
 * currencies, so a single total over two of them is a figure no document could
 * be raised for. The one converted figure beside those totals is taken at the
 * LATEST rate on the read, and this says so.
 *
 * The `status` is why the shape is separate from `FxApplied` rather than a
 * nullable version of it. A dated report answers `null` for "nothing needed
 * converting", and that one word cannot also carry "a figure needed a rate and
 * the table holds none" — which is not a refusal here, only a converted total
 * the screen does not draw.
 *
 * Hand-written for the same reason `FxApplied` is: the document hoists this
 * shape once per report, and the SPA renders all of them through one
 * component.
 */
export const FxDisplayStatus = z.enum(["display_converted", "not_needed", "unavailable"]);
export const FxDisplay = z.object({
    status: FxDisplayStatus,
    target_currency: CurrencySchema,
    usd_to_cad: z.number().positive().nullable(),
    as_of: IsoDate.nullable(),
});
/**
 * Convert minor units between CAD and USD at a given rate. Rounds once, at the
 * end, to an integer — the only place currency conversion happens on the read
 * path, so the rounding rule cannot differ between two reports.
 */
export function convertMinor(amountMinor, from, to, usdToCad) {
    if (from === to)
        return amountMinor;
    return from === "USD" ? Math.round(amountMinor * usdToCad) : Math.round(amountMinor / usdToCad);
}
//# sourceMappingURL=fx.js.map